import { useSyncExternalStore, useCallback, useEffect } from 'react';

var assetRegistry = /* @__PURE__ */ new Map();
var numericToStringIdRegistry = /* @__PURE__ */ new Map();
var nextAssetId = 1;
var assetMetadataRegistry = /* @__PURE__ */ new Map();
var srcToAssetIdsRegistry = /* @__PURE__ */ new Map();
function registerAsset(src, assetStringId) {
  const id = nextAssetId++;
  assetRegistry.set(id, src);
  if (assetStringId) {
    numericToStringIdRegistry.set(id, assetStringId);
  }
  return id;
}
function getAssetSrc(assetId) {
  return assetRegistry.get(assetId) || "";
}
function clearAssetRegistry() {
  assetRegistry.clear();
  numericToStringIdRegistry.clear();
  nextAssetId = 1;
  assetMetadataRegistry.clear();
  srcToAssetIdsRegistry.clear();
}
function getAssetStringId(numericId) {
  return numericToStringIdRegistry.get(numericId);
}
function setAssetMetadata(assetId, metadata, src) {
  const existing = assetMetadataRegistry.get(assetId) || {};
  assetMetadataRegistry.set(assetId, { ...existing, ...metadata });
  if (src) {
    if (!srcToAssetIdsRegistry.has(src)) {
      srcToAssetIdsRegistry.set(src, /* @__PURE__ */ new Set());
    }
    srcToAssetIdsRegistry.get(src).add(assetId);
  }
}
function getAssetMetadata(assetId) {
  return assetMetadataRegistry.get(assetId);
}
function updateAssetColor(src, color) {
  const colorScore = (color.r - color.g) / (color.r + color.g + color.b + 1);
  const assetIds = srcToAssetIdsRegistry.get(src);
  if (assetIds) {
    assetIds.forEach((assetId) => {
      setAssetMetadata(assetId, { color, colorScore });
    });
  }
}
function getAssetIdsBySrc(src) {
  return srcToAssetIdsRegistry.get(src) || /* @__PURE__ */ new Set();
}
var currentState = { images: [], stateVersion: 0 };
var listeners = /* @__PURE__ */ new Set();
function emitChange() {
  listeners.forEach((listener) => listener());
}
function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
function getSnapshot() {
  return currentState;
}
function syncFromWasm(wasm) {
  const wasmVersion = wasm.getStateVersion();
  if (wasmVersion === currentState.stateVersion) return;
  const count = wasm.getObjectCount();
  const images = [];
  for (let i = 0; i < count; i++) {
    const objectId = wasm.getObjectIdAtIndex(i);
    if (objectId === 0) continue;
    const assetId = wasm.getObjectAssetId(objectId);
    const src = getAssetSrc(assetId);
    if (src) {
      images.push({
        id: `img-${objectId}`,
        x: wasm.getObjectX(objectId),
        y: wasm.getObjectY(objectId),
        width: wasm.getObjectWidth(objectId),
        height: wasm.getObjectHeight(objectId),
        src
      });
    }
  }
  currentState = { images, stateVersion: wasmVersion };
  emitChange();
}
function useCanvasStore() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
function useCanvasActions(wasm) {
  const moveObject = useCallback(
    (objectId, x, y) => {
      if (!wasm) return;
      wasm.moveObject(objectId, x, y);
      syncFromWasm(wasm);
    },
    [wasm]
  );
  const resizeObject = useCallback(
    (objectId, x, y, width, height) => {
      if (!wasm) return;
      wasm.resizeObject(objectId, x, y, width, height);
      syncFromWasm(wasm);
    },
    [wasm]
  );
  const batchMoveObjects = useCallback(
    (moves) => {
      if (!wasm || moves.length === 0) return;
      wasm.beginBatchMove();
      moves.forEach(({ objectId, x, y }) => {
        const oldX = wasm.getObjectX(objectId);
        const oldY = wasm.getObjectY(objectId);
        wasm.addToBatchMove(objectId, oldX, oldY, x, y);
      });
      wasm.endBatchMove();
      syncFromWasm(wasm);
    },
    [wasm]
  );
  const batchResizeObjects = useCallback(
    (resizes) => {
      if (!wasm || resizes.length === 0) return;
      wasm.beginBatchResize();
      resizes.forEach(({ objectId, x, y, width, height }) => {
        const oldX = wasm.getObjectX(objectId);
        const oldY = wasm.getObjectY(objectId);
        const oldWidth = wasm.getObjectWidth(objectId);
        const oldHeight = wasm.getObjectHeight(objectId);
        wasm.addToBatchResize(objectId, oldX, oldY, oldWidth, oldHeight, x, y, width, height);
      });
      wasm.endBatchResize();
      syncFromWasm(wasm);
    },
    [wasm]
  );
  const deleteObject = useCallback(
    (objectId) => {
      if (!wasm) return;
      wasm.deleteObject(objectId);
      syncFromWasm(wasm);
    },
    [wasm]
  );
  const deleteObjects = useCallback(
    (objectIds) => {
      if (!wasm || objectIds.length === 0) return;
      objectIds.forEach((id) => wasm.deleteObject(id));
      syncFromWasm(wasm);
    },
    [wasm]
  );
  const undo = useCallback(() => {
    if (!wasm) return false;
    if (!wasm.canUndo()) return false;
    wasm.undo();
    syncFromWasm(wasm);
    return true;
  }, [wasm]);
  const redo = useCallback(() => {
    if (!wasm) return false;
    if (!wasm.canRedo()) return false;
    wasm.redo();
    syncFromWasm(wasm);
    return true;
  }, [wasm]);
  const canUndo = useCallback(() => {
    if (!wasm) return false;
    return wasm.canUndo();
  }, [wasm]);
  const canRedo = useCallback(() => {
    if (!wasm) return false;
    return wasm.canRedo();
  }, [wasm]);
  return {
    moveObject,
    resizeObject,
    batchMoveObjects,
    batchResizeObjects,
    deleteObject,
    deleteObjects,
    undo,
    redo,
    canUndo,
    canRedo
  };
}
function useUndoRedoShortcuts(wasm, onUndoRedo) {
  useEffect(() => {
    if (!wasm) return;
    const handleKeyDown = (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        if (wasm.canUndo()) {
          wasm.undo();
          syncFromWasm(wasm);
          onUndoRedo?.();
        }
      } else if (isMod && e.key === "z" && e.shiftKey || isMod && e.key === "y") {
        e.preventDefault();
        if (wasm.canRedo()) {
          wasm.redo();
          syncFromWasm(wasm);
          onUndoRedo?.();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [wasm, onUndoRedo]);
}
function initializeCanvasWithImages(wasm, images) {
  images.forEach((img) => {
    const assetId = registerAsset(img.src);
    wasm.addObject(img.x, img.y, img.width, img.height, assetId, 1);
  });
  syncFromWasm(wasm);
}
function getNumericId(id) {
  const match = id.match(/img-(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

export { clearAssetRegistry, getAssetIdsBySrc, getAssetMetadata, getAssetSrc, getAssetStringId, getNumericId, initializeCanvasWithImages, registerAsset, setAssetMetadata, syncFromWasm, updateAssetColor, useCanvasActions, useCanvasStore, useUndoRedoShortcuts };
//# sourceMappingURL=chunk-UEPGDLLX.js.map
//# sourceMappingURL=chunk-UEPGDLLX.js.map